import React, { useState, useMemo } from 'react';
import { 
  CalendarDays, 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Sparkles, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  Car, 
  User, 
  Coins, 
  Phone, 
  Send 
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { formatDateFR } from '../../shared/utils/helpers';
import Modal from '../../shared/components/Modal';

export default function CalendarPage() {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const { data: rentals } = useCollection('rentals');
  const { data: cars } = useCollection('cars');
  const { data: clients } = useCollection('clients');
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRental, setSelectedRental] = useState(null);
  const [carFilter, setCarFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [settings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('appSettings') || '{}') || { currency: 'MAD' };
    } catch {
      return { currency: 'MAD' };
    }
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const monthNames = isAr
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  const dayNamesShort = isAr
    ? ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد']
    : ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const dayNamesShortMobile = isAr
    ? ['ن', 'ث', 'ر', 'خ', 'ج', 'س', 'ح']
    : ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

  const gridCells = useMemo(() => {
    const cells = [];
    const firstDayInstance = new Date(year, month, 1);
    
    // getDay returns 0 for Sunday, 1 for Monday, etc.
    const startDayOfWeek = firstDayInstance.getDay();
    // Shift to start week on Monday (0=Mon, 6=Sun)
    const mondayShift = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = mondayShift - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    }
    
    const currentMonthLastDay = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= currentMonthLastDay; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const totalCells = 42;
    const nextCellsCount = totalCells - cells.length;
    for (let i = 1; i <= nextCellsCount; i++) {
      cells.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return cells;
  }, [year, month]);

  const getRentalsForDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const dTime = d.getTime();

    return rentals.filter(r => {
      if (r.status === 'cancelled') return false;
      if (carFilter !== 'all' && r.carId !== carFilter) return false;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const clientMatch = (r.clientName || '').toLowerCase().includes(query);
        const carMatch = (r.carName || '').toLowerCase().includes(query);
        if (!clientMatch && !carMatch) return false;
      }
      
      const start = new Date(r.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(r.endDate || r.startDate); // fallback for open contracts
      end.setHours(0, 0, 0, 0);
      
      // If it's open contract and active, check if the date is after start date
      if (r.isOpenContract && r.status === 'active') {
        return dTime >= start.getTime() && dTime <= today.getTime();
      }
      
      return dTime >= start.getTime() && dTime <= end.getTime();
    });
  };

  const getAlertStatus = (rental) => {
    if (rental.status === 'completed') return 'completed';
    if (rental.status === 'active') {
      if (rental.endDate && rental.endDate < todayStr && !rental.isOpenContract) return 'overdue';
      return 'active';
    }
    return 'upcoming';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'overdue': 
        return 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-650 hover:to-rose-700 text-white shadow-sm border border-red-400 animate-pulse';
      case 'active': 
        return 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-sm';
      case 'completed': 
        return 'bg-gradient-to-r from-slate-400 to-slate-500 hover:from-slate-500 hover:to-slate-600 text-white opacity-85 hover:opacity-100 border-l-2 border-slate-650';
      case 'upcoming': 
      default: 
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-sm';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'overdue': return isAr ? '⚠️ متأخر' : '⚠️ Retard';
      case 'active': return isAr ? '🟢 جارية' : '🟢 En Cours';
      case 'completed': return isAr ? '⚫ مكتملة' : '⚫ Terminée';
      case 'upcoming': return isAr ? '🔵 محجوزة' : '🔵 Réservée';
      default: return '';
    }
  };

  const getWhatsAppLink = (phone, carName, clientName, endDate) => {
    if (!phone) return '';
    let clean = phone.replace(/\s+/g, '');
    if (clean.startsWith('0')) clean = '212' + clean.slice(1);
    if (clean.startsWith('+')) clean = clean.slice(1);
    const message = isAr
      ? `السلام عليكم ${clientName}، تذكركم وكالتكم بخصوص كراء سيارة ${carName}. تاريخ الإرجاع كان متوقعاً يوم ${endDate}. شكراً للاتصال بنا للتسوية.`
      : `Salam ${clientName}, ${settings.companyName || 'Notre agence'} vous rappelle concernant la location du véhicule ${carName}. La restitution était prévue le ${endDate}. Merci de nous contacter pour régulariser.`;
    return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  };

  const isTodayDate = (date) => {
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  const getClientPhoneForRental = (rental) => {
    const client = clients.find(c => c.id === rental.clientId);
    return client?.phone || rental.clientPhone || '';
  };

  const clientPhone = selectedRental ? getClientPhoneForRental(selectedRental) : '';

  return (
    <div>
      {/* Header */}
      <div className="bg-slate-900 rounded-2xl p-6 shadow-xl mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6 border border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CalendarDays className="w-7 h-7 text-blue-400" />
            <span>{t('calendar_title') || (isAr ? 'مخطط الحجوزات' : 'Timeline Flotte')}</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {t('calendar_desc') || (isAr ? 'تتبع يومي لحالة السيارات والحجوزات' : 'Suivi visuel quotidien du planning de location')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button 
            onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
            className="w-11 h-11 flex items-center justify-center bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 shadow-sm text-white font-bold transition cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="px-6 py-2.5 bg-blue-600/20 border border-blue-500/30 text-blue-400 rounded-xl font-bold text-base shadow min-w-[190px] text-center uppercase tracking-wide">
            {`${monthNames[month]} ${year}`}
          </div>
          <button 
            onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
            className="w-11 h-11 flex items-center justify-center bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 shadow-sm text-white font-bold transition cursor-pointer"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button 
            onClick={() => {
              const now = new Date();
              setCurrentMonth(now);
              setSelectedDate(now);
            }}
            className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-650 border border-emerald-450 rounded-xl text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition cursor-pointer"
          >
            {t('today') || (isAr ? 'اليوم' : "Aujourd'hui")}
          </button>
        </div>
      </div>

      {/* Filter Bar & Quick Stats */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-4 flex-wrap">
          <div className="relative max-w-xs w-full min-w-[180px]">
            <Search className={`absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400`} />
            <input 
              type="text" 
              placeholder={t('search_placeholder') || (isAr ? 'بحث...' : 'Rechercher...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full ${isAr ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 bg-gray-50 border border-gray-250 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
            />
          </div>
          <div className="h-8 w-px bg-gray-200 hidden md:block" />
          <select 
            value={carFilter} 
            onChange={e => setCarFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white cursor-pointer transition"
          >
            <option value="all">{isAr ? 'جميع السيارات' : 'Tous les véhicules'}</option>
            {cars.map(car => (
              <option key={car.id} value={car.id}>{`${car.brand} ${car.model} (${car.plate})`}</option>
            ))}
          </select>
          {(searchQuery || carFilter !== 'all') && (
            <button 
              onClick={() => { setSearchQuery(''); setCarFilter('all'); }} 
              className="text-xs text-red-500 hover:text-red-700 font-bold underline"
            >
              {isAr ? 'إلغاء الفلتر' : 'Effacer'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs font-bold text-gray-700 bg-gray-50 px-4 py-2 rounded-xl border border-gray-200 flex-wrap">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
            <span>{`${isAr ? 'نشطة' : 'Actives'}: ${rentals.filter(r => r.status === 'active' && (!r.endDate || r.endDate >= todayStr)).length}`}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_#ef4444] animate-pulse" />
            <span>{`${isAr ? 'متأخرة' : 'En retard'}: ${rentals.filter(r => r.status === 'active' && r.endDate && r.endDate < todayStr && !r.isOpenContract).length}`}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_#3b82f6]" />
            <span>{`${isAr ? 'محجوزة' : 'Réservées'}: ${rentals.filter(r => r.status !== 'completed' && r.startDate > todayStr).length}`}</span>
          </span>
        </div>
      </div>

      {/* Grid Calendar */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden table-container">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-slate-50">
          {dayNamesShort.map((name, idx) => (
            <div key={name} className="py-3 sm:py-4 text-center text-xs font-extrabold text-slate-500 uppercase tracking-wider">
              <span className="hidden sm:inline">{name}</span>
              <span className="inline sm:hidden">{dayNamesShortMobile[idx]}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 bg-gray-200 gap-[1px]">
          {gridCells.map((cell, idx) => {
            const date = cell.date;
            const isCurrent = cell.isCurrentMonth;
            const isTodayCell = isTodayDate(date);
            const dateRentals = getRentalsForDate(date);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            const isSelectedDate = selectedDate && 
              date.getDate() === selectedDate.getDate() && 
              date.getMonth() === selectedDate.getMonth() && 
              date.getFullYear() === selectedDate.getFullYear();

            let cellClass = 'h-[58px] md:min-h-[140px] p-1 md:p-2.5 flex flex-col gap-1 md:gap-1.5 transition-all duration-200 ease-out group relative cursor-pointer ';
            if (!isCurrent) cellClass += 'bg-slate-50/40 opacity-60 border-dashed ';
            else if (isTodayCell) cellClass += 'bg-blue-50/40 z-10 before:absolute before:inset-0 before:border-2 before:border-blue-500 before:pointer-events-none ';
            else if (isWeekend) cellClass += 'bg-slate-55 bg-slate-50/80 hover:bg-white hover:shadow-lg hover:-translate-y-0.5 hover:z-20 ';
            else cellClass += 'bg-white hover:shadow-lg hover:-translate-y-0.5 hover:z-20 ';

            if (isSelectedDate) {
              cellClass += 'ring-2 ring-blue-500/85 bg-blue-50/25 z-20 ';
            }

            return (
              <div 
                key={idx} 
                className={cellClass}
                onClick={() => setSelectedDate(date)}
              >
                {/* Desktop View Content (hidden on mobile) */}
                <div className="hidden md:flex flex-col h-full w-full gap-1.5">
                  {/* Cell Header */}
                  <div className="flex items-center justify-between mb-1 relative z-10">
                    <span className={`text-sm font-extrabold ${
                      isTodayCell 
                        ? 'w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/30' 
                        : isCurrent ? 'text-gray-950 group-hover:text-blue-600 transition-colors' : 'text-gray-400'
                    }`}>
                      {date.getDate()}
                    </span>
                    {dateRentals.length > 0 && (
                      <span className="text-[10px] font-extrabold text-blue-755 bg-blue-50 border border-blue-200 px-1.5 py-0.2 rounded-full shadow-sm">
                        {`${dateRentals.length} ${isAr ? 'عقود' : (dateRentals.length > 1 ? 'locs' : 'loc')}`}
                      </span>
                    )}
                  </div>
                  
                  {/* Bookings */}
                  <div className="flex-1 overflow-y-auto space-y-1 max-h-[95px] pr-0.5 scrollbar-thin relative z-10">
                    {dateRentals.map(rental => {
                      const status = getAlertStatus(rental);
                      const style = getStatusStyle(status);
                      
                      return (
                        <div
                          key={rental.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRental(rental);
                          }}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold truncate cursor-pointer transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1 ${style}`}
                        >
                          <Car className="w-3 h-3 shrink-0 opacity-80" />
                          <span className="truncate">{`${rental.clientName.split(' ')[0]} - ${rental.carName.split(' ')[0]}`}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile View Content (hidden on desktop) */}
                <div className="flex md:hidden flex-col items-center justify-between h-full py-0.5">
                  <span className={`text-xs font-extrabold w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    isTodayCell 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : isSelectedDate
                        ? 'border-2 border-blue-500 text-blue-600 font-black'
                        : isCurrent ? 'text-gray-950' : 'text-gray-400'
                  }`}>
                    {date.getDate()}
                  </span>
                  
                  {/* Booking dots indicator */}
                  <div className="flex gap-0.5 justify-center h-2 items-center">
                    {dateRentals.slice(0, 3).map(rental => {
                      const status = getAlertStatus(rental);
                      let dotColor = 'bg-blue-500';
                      if (status === 'overdue') dotColor = 'bg-red-500 animate-pulse';
                      else if (status === 'active') dotColor = 'bg-emerald-500';
                      else if (status === 'completed') dotColor = 'bg-slate-400';
                      return <span key={rental.id} className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />;
                    })}
                    {dateRentals.length > 3 && (
                      <span className="text-[8px] font-bold text-gray-500 leading-none">+</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Rentals Panel (Mobile only, fixed height to prevent layout shifts) */}
      <div className="block md:hidden mt-4 bg-white border border-gray-250 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between pb-2.5 border-b border-gray-100 mb-3">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            {isAr ? 'حجوزات تاريخ:' : 'Locations du :'} {selectedDate.toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <span className="text-xs font-extrabold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
            {`${getRentalsForDate(selectedDate).length} ${isAr ? 'عقود' : (getRentalsForDate(selectedDate).length > 1 ? 'locs' : 'loc')}`}
          </span>
        </div>
        
        {/* Scrollable list with fixed height to prevent layout shifts */}
        <div className="h-[140px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
          {getRentalsForDate(selectedDate).map(rental => {
            const status = getAlertStatus(rental);
            const style = getStatusStyle(status);
            const label = getStatusLabel(status);
            
            return (
              <div
                key={rental.id}
                onClick={() => setSelectedRental(rental)}
                className="p-3 bg-gray-50 hover:bg-gray-100 border border-gray-150 rounded-xl cursor-pointer flex items-center justify-between gap-3 transition-colors active:bg-gray-200"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Car className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-gray-900 truncate">{rental.carName}</div>
                    <div className="text-[10px] text-gray-500 truncate">{rental.clientName}</div>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black shrink-0 ${style.split(' ').filter(c => !c.includes('hover') && !c.includes('animate')).join(' ')}`}>
                  {label}
                </span>
              </div>
            );
          })}
          {getRentalsForDate(selectedDate).length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 p-4">
              <span className="text-xl mb-1">📅</span>
              <div className="text-xs font-bold text-gray-400">
                {isAr ? 'لا توجد حجوزات في هذا التاريخ' : 'Aucune location pour cette date'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend panel */}
      <div className="bg-slate-900 rounded-2xl p-6 mt-6 shadow-xl border border-slate-800">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
          <div className="p-2 bg-blue-500/20 rounded-xl">
            <Sparkles className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white uppercase tracking-wider">{isAr ? 'دليل الألوان' : 'Légende & Statuts'}</h3>
            <p className="text-xs text-slate-400">{isAr ? 'تعرف على حالة العقود من خلال الألوان' : 'Signification des codes couleurs du planning'}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-slate-850 bg-slate-800/40 border border-slate-700 p-4 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shrink-0 shadow-sm">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{isAr ? 'جارية' : 'Location en cours'}</p>
              <p className="text-[11px] text-slate-400 mt-1">{isAr ? 'السيارة حاليا عند العميل' : 'Le véhicule est actuellement chez le client.'}</p>
            </div>
          </div>
          <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-rose-600 flex items-center justify-center shrink-0 shadow-sm animate-pulse">
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-red-400">{isAr ? 'متأخرة' : 'Restitution en retard'}</p>
              <p className="text-[11px] text-slate-400 mt-1">{isAr ? 'تاريخ العودة الافتراضي قد مضى' : "Le délai de retour prévu est dépassé !"}</p>
            </div>
          </div>
          <div className="bg-slate-850 bg-slate-800/40 border border-slate-700 p-4 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shrink-0 shadow-sm">
              <CalendarDays className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{isAr ? 'محجوزة مسبقاً' : 'Réservations futures'}</p>
              <p className="text-[11px] text-slate-400 mt-1">{isAr ? 'عقود تبدأ في تاريخ لاحق' : 'Locations prévues pour les jours à venir.'}</p>
            </div>
          </div>
          <div className="bg-slate-850 bg-slate-800/40 border border-slate-700 p-4 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-500 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{isAr ? 'مكتملة' : 'Locations terminées'}</p>
              <p className="text-[11px] text-slate-400 mt-1">{isAr ? 'عقود تم إرجاعها وأرشفتها' : 'Véhicules restitués et contrats clôturés.'}</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-600/10 border border-blue-500/20 rounded-xl p-4 flex gap-3 items-start">
          <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-sm text-blue-100/90">
            <b className="text-blue-300">{isAr ? 'نصيحة: ' : 'Astuce: '}</b>
            {isAr ? 'انقر على أي مستطيل كراء لعرض تفاصيل العقد كاملة والاتصال بالعميل مباشرة.' : 'Cliquez sur n\'importe quelle location dans le calendrier pour ouvrir ses détails et contacter le client.'}
          </p>
        </div>
      </div>

      {/* Contract Detail Modal */}
      {selectedRental && (
        <Modal
          title={t('contract_title') || (isAr ? 'تفاصيل عقد الكراء' : 'Détails du Contrat')}
          onClose={() => setSelectedRental(null)}
        >
          <div className="space-y-6">
            {/* Top summary banner */}
            <div className={`p-5 rounded-2xl border flex items-center justify-between shadow-sm ${
              getAlertStatus(selectedRental) === 'overdue' ? 'bg-red-50 border-red-200 text-red-950' :
              getAlertStatus(selectedRental) === 'active' ? 'bg-emerald-50 border-emerald-200 text-emerald-950' :
              getAlertStatus(selectedRental) === 'completed' ? 'bg-gray-50 border-gray-200 text-gray-950' :
              'bg-blue-50 border-blue-200 text-blue-950'
            }`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center">
                  <Car className="w-6 h-6 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t('vehicle') || (isAr ? 'السيارة' : 'Voiture')}</p>
                  <p className="text-xl font-extrabold text-gray-900">{selectedRental.carName}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">{t('contract_status') || (isAr ? 'حالة العقد' : 'Statut')}</p>
                <span className={`inline-flex px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-sm ${
                  getStatusStyle(getAlertStatus(selectedRental)).split(' ').filter(c => !c.includes('hover') && !c.includes('animate')).join(' ')
                }`}>
                  {getStatusLabel(getAlertStatus(selectedRental))}
                </span>
              </div>
            </div>

            {/* Grid details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client info */}
              <div className="border border-gray-200 rounded-2xl p-5 space-y-3 bg-white shadow-sm">
                <h4 className="text-xs font-bold text-gray-400 uppercase border-b border-gray-100 pb-2 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{t('client_info') || (isAr ? 'معلومات العميل' : 'Locataire')}</span>
                </h4>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{t('full_name')}</strong>
                  <span>{selectedRental.clientName}</span>
                </div>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{t('departure')}</strong>
                  <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">{formatDateFR(selectedRental.startDate)}</span>
                </div>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{t('return_expected')}</strong>
                  <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">
                    {selectedRental.isOpenContract ? (isAr ? 'عقد مفتوح ⏳' : 'Contrat ouvert ⏳') : formatDateFR(selectedRental.endDate)}
                  </span>
                </div>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{isAr ? 'المدة الإجمالية' : 'Durée total'}</strong>
                  <span className="font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs">{`${selectedRental.days} ${isAr ? 'يوم' : 'jours'}`}</span>
                </div>
              </div>
              
              {/* Finances */}
              <div className="border border-gray-200 rounded-2xl p-5 space-y-3 bg-white shadow-sm">
                <h4 className="text-xs font-bold text-gray-400 uppercase border-b border-gray-100 pb-2 mb-3 flex items-center gap-2">
                  <Coins className="w-4 h-4 text-gray-400" />
                  <span>{isAr ? 'الحساب والمالية' : 'Tarifs & Réglement'}</span>
                </h4>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{isAr ? 'السعر اليومي' : 'Tarif journalier'}</strong>
                  <span>{`${selectedRental.dailyRate} ${settings.currency} / j`}</span>
                </div>
                <div className="text-sm text-gray-755 text-gray-700 flex justify-between">
                  <strong className="text-gray-900">{isAr ? 'مبلغ العربون' : 'Acompte versé'}</strong>
                  <span>{`${selectedRental.deposit || 0} ${settings.currency}`}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                  <strong className="text-gray-900 font-extrabold uppercase text-xs">{t('total_amount') || (isAr ? 'المجموع الكلي' : 'Montant total')}</strong>
                  <span className="font-black text-blue-700 text-xl tracking-tight bg-blue-50 px-3 py-1 rounded-xl border border-blue-100 font-mono">
                    {`${selectedRental.totalAmount} ${settings.currency}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 border-t border-gray-100 pt-5 mt-2 flex-wrap">
              <button
                onClick={() => setSelectedRental(null)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-bold transition"
              >
                {t('close') || (isAr ? 'إغلاق' : 'Fermer')}
              </button>
              
              {getAlertStatus(selectedRental) !== 'completed' && clientPhone && (
                <div className="flex gap-2">
                  <a
                    href={`tel:${clientPhone}`}
                    className="flex items-center gap-2 bg-blue-650 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition shadow-sm"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{isAr ? 'اتصال' : 'Appeler'}</span>
                  </a>
                  <a
                    href={getWhatsAppLink(clientPhone, selectedRental.carName, selectedRental.clientName, selectedRental.endDate)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition shadow-sm"
                  >
                    <Send className="w-4 h-4" />
                    <span>{isAr ? 'واتساب' : 'WhatsApp'}</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
